import { useState } from 'react'
import { Users, RefreshCw, Edit, Trash2 } from 'lucide-react'
import { Time } from '../tipos/time'
import { useDeleteTime } from '../hooks/useTime'
import { usePagination } from '../../../hooks/usePagination'
import { PaginationControls } from '../../../componentesCompartilhados/paginacao/PaginationControls'

interface ListaTimeProps {
  times: Time[]
  isLoading: boolean
  onRefresh: () => void
  onEditTime: (time: Time) => void
}

export function ListaTime({ times, isLoading, onRefresh, onEditTime }: ListaTimeProps) {
  const deleteMutation = useDeleteTime()
  const [timeSelecionado, setTimeSelecionado] = useState<Time | null>(null)

  const {
    paginatedItems: timesPaginados,
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    nextPage,
    previousPage,
  } = usePagination(times, { totalItems: times.length, itemsPerPage: 10 })

  const handleDelete = (time: Time) => {
    setTimeSelecionado(null)
    deleteMutation.mutate(time.id)
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-gray-600">Carregando times...</p>
        </div>
      </div>
    )
  }

  if (!times.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum time encontrado</h3>
          <p className="text-gray-600">Cadastre um time para começar.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Times cadastrados</h3>
            <p className="text-sm text-gray-600">{times.length} time(s) encontrado(s)</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descrição</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {timesPaginados.map((time) => (
              <tr key={time.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{time.descricao}</span>
                    {time.externo && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        Externo
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEditTime(time)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setTimeSelecionado(time)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={times.length}
        startIndex={startIndex}
        endIndex={endIndex}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        itemLabel="time"
      />

      {timeSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Excluir time</h3>
            <p className="text-sm text-gray-600 mb-4">
              Tem certeza que deseja excluir o time <strong>{timeSelecionado.descricao}</strong>? Essa ação não poderá
              ser desfeita.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTimeSelecionado(null)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(timeSelecionado)}
                className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all duration-200"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
